import { tr } from '../i18n';
import type { TranslationKey } from '../locales/en';

export type CheckoutErrorKind = 'validation' | 'business' | 'payment' | 'system';
export type CheckoutErrorAction = 'retry' | 'refresh';

export interface CheckoutErrorDescriptor {
  kind: CheckoutErrorKind;
  title: string;
  titleKey?: TranslationKey;
  message: string;
  messageKey?: TranslationKey;
  messageValues?: Record<string, unknown>;
  code?: string;
  field?: string;
  action?: CheckoutErrorAction;
}

export function resolveCheckoutErrorMessages(error: CheckoutErrorDescriptor): {
  title: string;
  message: string;
} {
  return {
    title: error.titleKey === undefined ? error.title : tr(error.titleKey),
    message: error.messageKey === undefined
      ? error.message
      : tr(error.messageKey, error.messageValues ?? {}),
  };
}

export class CheckoutFlowError extends Error {
  readonly descriptor: CheckoutErrorDescriptor;

  constructor(descriptor: CheckoutErrorDescriptor) {
    super(descriptor.message);
    this.name = 'CheckoutFlowError';
    this.descriptor = descriptor;
  }
}

export const CHECKOUT_BLOCK_CODES = [
  'PRODUTO_INATIVO',
  'PRODUTO_NAO_ACEITA_AFILIADOS',
  'PRODUTOR_CONFIGURACAO_FINANCEIRA_PENDENTE',
  'COPRODUTOR_CONFIGURACAO_FINANCEIRA_PENDENTE',
  'AFILIADO_CONFIGURACAO_FINANCEIRA_PENDENTE',
  'AFILIADO_AUTO_COMPRA',
  'SEED_CONFIGURACAO_FINANCEIRA_PENDENTE',
  'COPRODUTOR_CONTA_STRIPE_INCOMPATIVEL',
  'AFILIADO_CONTA_STRIPE_INCOMPATIVEL',
  'SEED_CONTA_STRIPE_INCOMPATIVEL',
  'METODO_NAO_SUPORTADO_PARA_REGIME',
  'MOEDA_NAO_SUPORTADA_PARA_REGIME',
  'CONFIGURACAO_FINANCEIRA_ALTERADA',
  'PRICING_TOKEN_INVALIDO',
  'COTACAO_INDISPONIVEL',
  'METODO_NAO_PERMITIDO_PARA_PRODUTO',
  'PARCELAMENTO_NAO_PERMITIDO',
  'CUPOM_INVALIDO',
  'PRECO_ABAIXO_DO_MINIMO',
  'PRECO_ALTERADO',
  'PLANO_INATIVO',
  'ASSINATURA_SOMENTE_CARTAO',
  'ASSINATURA_TRIAL_GRATUITO_INDISPONIVEL',
  'ASSINATURA_DUPLICADA',
  'ASSINATURA_EM_ATRASO',
  'ASSINATURA_TRIAL_JA_UTILIZADO',
  'ASSINATURA_SEM_ORDER_BUMP',
  'ASSINATURA_SEM_CUPOM',
  'FUNIL_INDISPONIVEL',
  'PAGAMENTO_SALVO_INDISPONIVEL',
] as const;

type CheckoutBlockCode = (typeof CHECKOUT_BLOCK_CODES)[number];

// Stripe card decline codes and card errors exposed by the checkout.
// https://docs.stripe.com/declines/codes
// Fraud, lost/stolen cards and block lists intentionally share a neutral message.
const PAYMENT_ERROR_KEYS = {
  approve_with_id: 'decline.contact_issuer',
  authentication_failure: 'decline.authentication_failure',
  authentication_not_handled: 'decline.authentication_required',
  authentication_required: 'decline.authentication_required',
  call_issuer: 'decline.contact_issuer',
  card_decline_rate_limit_exceeded: 'decline.card_decline_rate_limit_exceeded',
  card_declined: 'decline.default',
  card_not_supported: 'decline.card_not_supported',
  card_velocity_exceeded: 'decline.card_velocity_exceeded',
  currency_not_supported: 'decline.currency_not_supported',
  do_not_honor: 'decline.contact_issuer',
  do_not_try_again: 'decline.contact_issuer',
  duplicate_transaction: 'decline.duplicate_transaction',
  expired_card: 'decline.expired_card',
  fraudulent: 'decline.default',
  generic_decline: 'decline.default',
  incomplete_cvc: 'decline.invalid_cvc',
  incomplete_expiry: 'decline.invalid_expiry',
  incomplete_number: 'decline.invalid_number',
  incorrect_address: 'decline.incorrect_address',
  incorrect_cvc: 'decline.incorrect_cvc',
  incorrect_number: 'decline.incorrect_number',
  incorrect_pin: 'decline.contact_issuer',
  incorrect_postal_code: 'decline.incorrect_zip',
  incorrect_zip: 'decline.incorrect_zip',
  insufficient_funds: 'decline.insufficient_funds',
  invalid_account: 'decline.invalid_account',
  invalid_amount: 'decline.invalid_amount',
  invalid_cvc: 'decline.invalid_cvc',
  invalid_expiry_month: 'decline.invalid_expiry',
  invalid_expiry_year: 'decline.invalid_expiry',
  invalid_number: 'decline.invalid_number',
  invalid_pin: 'decline.contact_issuer',
  issuer_not_available: 'decline.issuer_not_available',
  lost_card: 'decline.default',
  merchant_blacklist: 'decline.default',
  mobile_device_authentication_required: 'decline.authentication_required',
  new_account_information_available: 'decline.invalid_account',
  no_action_taken: 'decline.contact_issuer',
  not_permitted: 'decline.contact_issuer',
  offline_pin_required: 'decline.contact_issuer',
  online_or_offline_pin_required: 'decline.contact_issuer',
  payment_intent_authentication_failure: 'decline.authentication_failure',
  pickup_card: 'decline.default',
  pin_try_exceeded: 'decline.contact_issuer',
  processing_error: 'decline.processing_error',
  reenter_transaction: 'decline.processing_error',
  restricted_card: 'decline.default',
  revocation_of_all_authorizations: 'decline.contact_issuer',
  revocation_of_authorization: 'decline.contact_issuer',
  security_violation: 'decline.contact_issuer',
  service_not_allowed: 'decline.contact_issuer',
  setup_intent_authentication_failure: 'decline.authentication_failure',
  stolen_card: 'decline.default',
  stop_payment_order: 'decline.contact_issuer',
  stripe_customer_unavailable: 'error.payment',
  testmode_decline: 'decline.testmode_decline',
  transaction_not_allowed: 'decline.contact_issuer',
  try_again_later: 'decline.try_again_later',
  withdrawal_count_limit_exceeded: 'decline.card_velocity_exceeded',
} as const satisfies Record<string, TranslationKey>;

function isCheckoutBlockCode(value: string | undefined): value is CheckoutBlockCode {
  return value !== undefined && (CHECKOUT_BLOCK_CODES as readonly string[]).includes(value);
}

function paymentMessageKey(code: string | undefined): TranslationKey | undefined {
  return code !== undefined && Object.hasOwn(PAYMENT_ERROR_KEYS, code)
    ? PAYMENT_ERROR_KEYS[code as keyof typeof PAYMENT_ERROR_KEYS]
    : undefined;
}

function blockMessageKey(code: CheckoutBlockCode): TranslationKey {
  return `block.${code}` as TranslationKey;
}

export function presentCheckoutBlock(code?: string): CheckoutErrorDescriptor {
  const messageKey = isCheckoutBlockCode(code) ? blockMessageKey(code) : 'error.checkoutUnavailable';
  const titleKey = code === 'PRECO_ALTERADO' ? 'error.priceChangedTitle' : 'error.businessTitle';
  return {
    kind: 'business',
    title: tr(titleKey),
    titleKey,
    message: tr(messageKey),
    messageKey,
    ...(code !== undefined ? { code } : {}),
    ...(code === 'PRECO_ALTERADO' ? { action: 'refresh' as const } : {}),
  };
}

export function checkoutBlockCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('bloqueioCodigo' in error)) {
    return undefined;
  }
  const code = (error as { readonly bloqueioCodigo?: unknown }).bloqueioCodigo;
  return typeof code === 'string' ? code : undefined;
}

export function presentCheckoutOrderError(
  error: unknown,
  fallbackKey: TranslationKey = 'error.payment',
): CheckoutErrorDescriptor {
  const code = checkoutBlockCode(error);
  if (code !== undefined) {
    return presentCheckoutBlock(code);
  }
  return presentLocalizedError({
    key: fallbackKey,
    kind: fallbackKey === 'error.offerUnavailable' ? 'business' : 'payment',
  });
}

export function presentPaymentError(input: {
  declineCode?: string;
  subscription?: boolean;
}): CheckoutErrorDescriptor {
  const normalizedCode = input.declineCode?.trim().toLowerCase();
  const messageKey = paymentMessageKey(normalizedCode) ?? 'decline.default';
  const message = tr(messageKey);
  return {
    kind: 'payment',
    title: tr('error.paymentTitle'),
    titleKey: 'error.paymentTitle',
    message: input.subscription ? tr('decline.subscriptionContext', { message }) : message,
    ...(!input.subscription
      ? { messageKey }
      : {}),
    ...(normalizedCode !== undefined ? { code: normalizedCode } : {}),
  };
}

export function presentProviderError(input: {
  code?: string;
  declineCode?: string;
  message?: string;
  fallbackKey: TranslationKey;
}): CheckoutErrorDescriptor {
  const normalizedCode = (input.declineCode ?? input.code)?.trim().toLowerCase();
  if (normalizedCode !== undefined && paymentMessageKey(normalizedCode) !== undefined) {
    return presentPaymentError({ declineCode: normalizedCode });
  }
  return {
    kind: 'payment',
    title: tr('error.paymentTitle'),
    titleKey: 'error.paymentTitle',
    message: tr(input.fallbackKey),
    messageKey: input.fallbackKey,
    ...(normalizedCode !== undefined ? { code: normalizedCode } : {}),
  };
}

export function presentValidationError(input: {
  key: TranslationKey;
  field: string;
}): CheckoutErrorDescriptor {
  return {
    kind: 'validation',
    title: tr('error.validationTitle'),
    titleKey: 'error.validationTitle',
    message: tr(input.key),
    messageKey: input.key,
    field: input.field,
  };
}

export function presentLocalizedError(input: {
  key: TranslationKey;
  kind?: CheckoutErrorKind;
  field?: string;
  action?: CheckoutErrorAction;
  values?: Record<string, unknown>;
}): CheckoutErrorDescriptor {
  return {
    kind: input.kind ?? 'system',
    title: tr(
      input.kind === 'validation'
        ? 'error.validationTitle'
        : input.kind === 'payment'
          ? 'error.paymentTitle'
          : input.kind === 'business'
            ? 'error.businessTitle'
            : 'error.systemTitle',
    ),
    titleKey:
      input.kind === 'validation'
        ? 'error.validationTitle'
        : input.kind === 'payment'
          ? 'error.paymentTitle'
          : input.kind === 'business'
            ? 'error.businessTitle'
            : 'error.systemTitle',
    message: tr(input.key, input.values ?? {}),
    messageKey: input.key,
    ...(input.values !== undefined ? { messageValues: input.values } : {}),
    ...(input.field !== undefined ? { field: input.field } : {}),
    ...(input.action !== undefined ? { action: input.action } : {}),
  };
}

export function presentSystemError(
  _error?: unknown,
  action?: CheckoutErrorAction,
): CheckoutErrorDescriptor {
  return {
    kind: 'system',
    title: tr('error.systemTitle'),
    titleKey: 'error.systemTitle',
    message: tr('state.errorBody'),
    messageKey: 'state.errorBody',
    ...(action !== undefined ? { action } : {}),
  };
}
